import { CellValueChangedEvent, RowClassParams, StatusPanelDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';

import { useAuthUser } from 'auth/hooks';
import { BomPartViewByType, ComponentStatus } from 'bom/enums';
import { IElement } from 'bom/types';
import { AgGridTable } from 'core/components';
import { NotificationPosition, TableLayoutType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { AgGridClientTableProvider } from 'core/states';
import { AgGridDataRow } from 'core/types';
import {
  AddLineButton,
  ImportBomButton,
  useViewByContext,
  ViewByButton,
} from 'jobs/components/JobDetails';
import { useGetJobParts, useJobPartActions } from 'jobs/hooks';
import { IJob, IJobPart, IJobPartListRequestPayload } from 'jobs/types';
import { PartDetails } from 'parts/components/PartDetails';
import { useCostDecimals } from 'pricing/hooks';

import { columnDef } from './columns';
import { mapOnCellValueChanged } from './mappers';
import {
  AddNotesButton,
  AddToJobButton,
  AddToQuoteButton,
  CopyMoveToElementButton,
  RemoveButton,
  RestoreButton,
  UpsertComponentButton,
} from './selectionActions';
import { onRowsReorder } from './table';
import { TotalsStatusBar } from './TotalsStatusBar';
import { aggFuncs } from './utils';

interface IProps {
  job: IJob;
  element?: IElement;
}

export const PartList = ({ job, element }: IProps) => {
  const [selectedJobPart, setSelectedJobPart] = useState<IJobPart | null>(null);

  const { user } = useAuthUser();
  const costDecimals = useCostDecimals();
  const { viewBy } = useViewByContext();
  const { upsertJobPart, reorderJobParts } = useJobPartActions({
    notificationPosition: NotificationPosition.TOP,
  });

  const isAlternateVersionEnabled = useMemo(
    () => AlternateVersionHelper.isAlternateVersionEnabled({ user }),
    [user]
  );

  const hasRowReordering = useMemo(
    () => !element?.id && viewBy === BomPartViewByType.PART,
    [element?.id, viewBy]
  );

  const defaultColDef = useMemo(() => ({ autoHeight: true }), []);

  const payload: IJobPartListRequestPayload = useMemo(
    () => ({
      bom: job.id,
      viewBy: { group: BomPartViewByType.PART },
      ...(element?.id && { filters: { elementId: element.id } }),
    }),
    [job.id, element?.id]
  );

  const {
    records,
    isLoading: isDataLoading,
    loadingPercentage,
    currentPage,
  } = useGetJobParts(payload, { fetchAllPages: true });

  const isDataLoadCompleted = useMemo(() => loadingPercentage === 100, [loadingPercentage]);

  const columnDefs = useMemo(
    () =>
      columnDef(
        job,
        element,
        (jobPart: IJobPart) => setSelectedJobPart(jobPart),
        isAlternateVersionEnabled,
        hasRowReordering,
        isDataLoadCompleted,
        costDecimals
      ),
    [
      isAlternateVersionEnabled,
      job,
      element,
      setSelectedJobPart,
      hasRowReordering,
      isDataLoadCompleted,
      costDecimals,
    ]
  );

  const onCellValueChanged = useCallback(
    async (
      _: AgGridReact,
      event: CellValueChangedEvent
    ): Promise<{ updatedRow?: AgGridDataRow }> => {
      const { success, error } = await upsertJobPart(
        mapOnCellValueChanged({ [event.colDef.field]: event.newValue }, job.id, event.data, element)
      );

      if (!success) {
        throw new Error(error);
      }

      return {};
    },
    [element, job.id, upsertJobPart]
  );

  const statusBar = useMemo<{
    statusPanels: StatusPanelDef[];
  }>(
    () => ({
      statusPanels: [
        {
          statusPanel: TotalsStatusBar,
          align: 'left',
        },
      ],
    }),
    []
  );

  const actionButtons = useCallback(() => {
    const buttons = [<AddLineButton key="addLineButton" jobId={job.id} element={element} />];

    if (!element) {
      buttons.push(<ImportBomButton key="importBomButton" job={job} />);
    }

    return buttons;
  }, [job, element]);

  const rightActionButtons = useMemo(() => {
    if (element?.id) {
      return [];
    }

    return [<ViewByButton key="viewByButton" />];
  }, [element?.id]);

  const selectionActionButtons = useCallback(
    () => [
      <CopyMoveToElementButton key="copyMoveToElementButton" jobId={job.id} />,
      <AddNotesButton key="addNotesButton" jobId={job.id} element={element} />,
      <AddToJobButton key="addToJobButton" />,
      <AddToQuoteButton key="addToQuoteButton" />,
      <UpsertComponentButton key="upsertComponentButton" />,
      <RemoveButton key="removeButton" />,
      <RestoreButton key="restoreButton" />,
    ],
    [job.id, element]
  );

  const getRowClass = useCallback((row: RowClassParams<IJobPart>) => {
    if (!row.data) {
      return '';
    }

    const classes = [];

    const status = row.data.status;

    if (status === ComponentStatus.DELETED) {
      classes.push('deletedRow disabledRow');
    }

    if (status === ComponentStatus.REJECTED) {
      classes.push('disabledRow');
    }

    return classes.join(' ');
  }, []);

  return (
    <AgGridClientTableProvider
      tableId="jobPartListTable"
      entityType="Line"
      layoutType={TableLayoutType.JOB_PART}
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      data={records}
      isDataLoading={isDataLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      aggFuncs={aggFuncs}
      getRowClass={getRowClass}
      onCellValueChanged={onCellValueChanged}
      onRowsReorder={hasRowReordering ? onRowsReorder(reorderJobParts) : undefined}
      hasRowReordering={hasRowReordering}
      hasLayoutActions
      headerCheckbox={isDataLoadCompleted}
      statusBar={statusBar}
    >
      <AgGridTable
        actionButtons={actionButtons}
        rightActionButtons={rightActionButtons}
        selectionActionButtons={selectionActionButtons}
      />

      <PartDetails
        part={selectedJobPart?.partArr}
        isOpen={!!selectedJobPart}
        onClose={() => setSelectedJobPart(null)}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
      />
    </AgGridClientTableProvider>
  );
};
